/**
 * Avisos breves no fundo do ecrã. Os de sucesso saem sozinhos ao fim de 4 s; os de erro ficam
 * até a pessoa os fechar (ou usar a ação, por exemplo «Tentar novamente»), são anunciados de
 * imediato pelos leitores de ecrã (role="alert") e podem dizer o motivo numa 2.ª linha.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { cx } from './cx';
import { useLocale } from './locale';
import { uiText } from './ui-text';

type Tone = 'neutral' | 'positive' | 'critical';

export interface ToastOptions {
  /** O motivo (por exemplo, a mensagem do servidor), por baixo da mensagem. */
  detail?: string | undefined;
  /** Uma ação no próprio aviso (fecha-o). */
  action?: { label: string; onClick: () => void } | undefined;
}

interface ToastItem extends ToastOptions {
  id: number;
  message: string;
  tone: Tone;
}

type Show = (message: string, tone?: Tone, options?: ToastOptions) => void;

const ToastContext = createContext<Show>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const t = uiText(useLocale());
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback<Show>(
    (message, tone = 'neutral', options = {}) => {
      const id = Date.now() + Math.random();
      setItems((xs) => [...xs.slice(-2), { id, message, tone, ...options }]);
      if (tone !== 'critical') window.setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );
  const item = (x: ToastItem) => (
    <div
      key={x.id}
      data-toast={x.tone}
      className={cx(
        'pointer-events-auto flex max-w-md items-start gap-3 px-5 py-3 text-sm shadow-lg',
        x.detail || x.action || x.tone === 'critical' ? 'rounded-[14px]' : 'rounded-full',
        x.tone === 'critical' ? 'bg-critical text-white' : 'bg-ink text-surface-0',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium">{x.message}</p>
        {x.detail && <p className="mt-0.5 opacity-90">{x.detail}</p>}
      </div>
      {x.action && (
        <button
          type="button"
          className="shrink-0 font-semibold underline underline-offset-2"
          onClick={() => {
            dismiss(x.id);
            x.action!.onClick();
          }}
        >
          {x.action.label}
        </button>
      )}
      {x.tone === 'critical' && (
        <button
          type="button"
          aria-label={t.close}
          className="-mr-2 shrink-0 rounded-full px-1.5 text-base leading-5 opacity-80 hover:opacity-100"
          onClick={() => dismiss(x.id)}
        >
          ×
        </button>
      )}
    </div>
  );
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6">
        <div role="alert" className="flex flex-col items-center gap-2">
          {items.filter((x) => x.tone === 'critical').map(item)}
        </div>
        <div aria-live="polite" role="status" className="flex flex-col items-center gap-2">
          {items.filter((x) => x.tone !== 'critical').map(item)}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
